import React, { useState, useEffect } from 'react';
import './App.css';

import { ROUTES_WITH_TITLES } from './constants';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import NavBarComponent from './components/NavBarComponent';
import { Helmet } from 'react-helmet';
import banner_image from './assets/banner_form.png';
import { IRouteItem } from './interfaces';
import { RoutePath } from './enums';
import ChallengeView from './views/ChallengeView'; 

// Metadata for the page
export const metadata = {
  title: "uOttaHack Live",
  description:
    "Join us to attend uOttaHack 8, the most anticipated hackathon in Ottawa, proudly celebrating 8 years of innovation, creativity, and community. This year, we’re leveling up the hacker experience with exciting new initiatives, exclusive workshops, inspiring keynote speakers, and unforgettable activities. Whether you're a seasoned hacker or a first-timer, uOttaHack 8 promises to be an incredible weekend of collaboration, learning, and fun.",
  openGraph: {
    type: 'website',
    title: "uOttaHack Live - 2026",
    description: "Join us to attend uOttaHack 8, the most anticipated hackathon in Ottawa.",
    url: "https://2026.uOttahack.com",
    images: [
      {
        url: banner_image,
        width: 1200,
        height: 630,
        alt: "uOttaHack 8",
      },
    ],
    siteName: "uOttaHack",
    locale: "en_US",
  },
  twitter: {
    card: "summary_large_image",
    title: "uOttaHack 8 - 2026",
    description: "Join us for uOttaHack 8; the most anticipated hackathon in Ottawa.",
    images: [
      {
        url: banner_image,
        width: 800,
        height: 418,
        alt: "uOttaHack 8",
      },
    ],
    site: "@uOttaHack",
  },
  appleWebApp: {
    title: "uOttaHack 8",
    statusBarStyle: "black-translucent",
  },
};

const App: React.FC = () => {
  const [routes, setRoutes] = useState<IRouteItem[]>(ROUTES_WITH_TITLES);

  useEffect(() => {
    const checkChallengesVisibility = () => {
      // Jan 16 at 10PM EST = Jan 17 at 3AM UTC (EST is UTC-5)
      const hackStartDate = new Date('2026-01-17T03:00:00Z');
      const now = new Date();
      const challengesVisible = now >= hackStartDate;

      // Filter routes to include/exclude challenges
      const updatedRoutes = ROUTES_WITH_TITLES.filter(route => {
        if (route.path === RoutePath.Challenge) {
          return challengesVisible;
        }
        return true;
      });

      // Add challenges route if it should be visible and not already present
      if (challengesVisible && !updatedRoutes.find(r => r.path === RoutePath.Challenge)) {
        const challengeIndex = ROUTES_WITH_TITLES.findIndex(r => r.path === RoutePath.Challenge);
        if (challengeIndex !== -1) {
          updatedRoutes.splice(challengeIndex, 0, {
            path: RoutePath.Challenge,
            title: 'Challenges',
            component: ChallengeView
          });
        }
      }

      setRoutes(updatedRoutes);
    };

    checkChallengesVisibility();
    const interval = setInterval(checkChallengesVisibility, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App">
      <Helmet>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta property="og:type" content={metadata.openGraph.type} />
        <meta property="og:title" content={metadata.openGraph.title} />
        <meta property="og:description" content={metadata.openGraph.description} />
        <meta property="og:url" content={metadata.openGraph.url} />
        <meta property="og:site_name" content={metadata.openGraph.siteName} />
        <meta property="og:locale" content={metadata.openGraph.locale} />
        <meta property="og:image" content={metadata.openGraph.images[0].url} />
        <meta property="og:image:width" content={metadata.openGraph.images[0].width.toString()} />
        <meta property="og:image:height" content={metadata.openGraph.images[0].height.toString()} />
        
        <meta name="twitter:card" content={metadata.twitter.card} />
        <meta name="twitter:title" content={metadata.twitter.title} />
        <meta name="twitter:description" content={metadata.twitter.description} />
        <meta name="twitter:image" content={metadata.twitter.images[0].url} />
        <meta name="twitter:image:width" content={metadata.twitter.images[0].width.toString()} />
        <meta name="twitter:image:height" content={metadata.twitter.images[0].height.toString()} />
        <meta name="twitter:site" content={metadata.twitter.site} />

        <meta name="apple-mobile-web-app-title" content={metadata.appleWebApp.title} />
        <meta name="apple-mobile-web-app-status-bar-style" content={metadata.appleWebApp.statusBarStyle} />
      </Helmet>
      <Router>
        <NavBarComponent />
        <div id="switch">
          <Switch>
            {routes.map((routeItem, index) => (
              <Route
                key={`${routeItem.title}-${index}`}
                exact={index === 0}
                path={routeItem.path}
                component={routeItem.component}
              />
            ))}
            <Redirect from="*" to="/" />
          </Switch>
        </div>
      </Router>
    </div>
  );
};

export default App;